import { Link, useLocation } from "@tanstack/react-router";
import { HomeIcon, PlateIcon, CompassIcon, CalendarIcon, HeartIcon } from "@/components/icons/NavIcons";
import { Map as MapIcon } from "lucide-react";
import { useGlobalMapFabHidden } from "@/lib/map-fab-visibility";
import { useLanguage } from "@/i18n/LanguageContext";
import { AutoT } from "@/i18n/AutoT";
import type { UIKey } from "@/i18n/ui";

type TabPath = "/" | "/eat" | "/do" | "/happening" | "/saved";

type Tab = {
  key: string;
  labelKey: UIKey;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  path: TabPath;
  match: (pathname: string) => boolean;
};

const tabs: Tab[] = [
  { key: "home",     labelKey: "bottom.home",     icon: HomeIcon,     path: "/",          match: (p) => p === "/" },
  { key: "eat",      labelKey: "bottom.eat",      icon: PlateIcon,    path: "/eat",       match: (p) => p.startsWith("/eat") },
  { key: "discover", labelKey: "bottom.discover", icon: CompassIcon,  path: "/do",        match: (p) => p.startsWith("/do") || p.startsWith("/ticket") },
  { key: "events",   labelKey: "bottom.events",   icon: CalendarIcon, path: "/happening", match: (p) => p.startsWith("/happening") },
  { key: "saved",    labelKey: "bottom.saved",    icon: HeartIcon,    path: "/saved",     match: (p) => p.startsWith("/saved") },
];

const desktopItems = tabs;

export function BottomNav() {
  const { pathname } = useLocation();
  const hideGlobalMapFab = useGlobalMapFabHidden();
  const { t } = useLanguage();

  return (
    <>
      {/* Mobile compact tab bar */}
      <div
        className="md:hidden fixed inset-x-0 bottom-0 z-40 pointer-events-none"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {/* iOS Safari re-rasterises backdrop-filter on fixed bars every scroll
            frame; `ios-no-blur` swaps to the opaque background there. */}
        <div className="pointer-events-auto bg-white/60 supports-[-webkit-touch-callout:none]:bg-white/95 backdrop-blur-2xl backdrop-saturate-150 ios-no-blur gpu-layer border-t border-white/40 shadow-[0_-4px_20px_-8px_rgba(15,23,42,0.2)]">

          <nav aria-label="Primary">
            <ul className="grid grid-cols-5 items-stretch">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = tab.match(pathname);
                return (
                  <li key={tab.key} className="flex">
                    <Link
                      to={tab.path}
                      aria-label={t(tab.labelKey)}
                      aria-current={active ? "page" : undefined}
                      className={`relative flex flex-1 flex-col items-center justify-center gap-1 py-2 transition-colors ${
                        active ? "text-navy" : "text-navy/70 hover:text-navy"
                      }`}
                    >
                      <span
                        className={`grid place-items-center size-9 rounded-full transition-all ${
                          active
                            ? "bg-navy text-white shadow-[0_4px_12px_-4px_rgba(15,23,42,0.45)] scale-105"
                            : "bg-transparent"
                        }`}
                      >
                        <Icon size={20} />
                      </span>
                      <span
                        className={`text-[10px] tracking-tight ${
                          active ? "font-bold text-navy" : "font-medium text-navy/75"
                        }`}
                      >
                        {t(tab.labelKey)}
                      </span>
                    </Link>
                  </li>
                );

              })}
            </ul>
          </nav>
        </div>
      </div>



      {/* Desktop: floating pill */}
      <nav
        aria-label="Primary"
        className="hidden md:flex fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] bg-navy/95 backdrop-blur-xl text-white px-2 py-2 rounded-full items-center gap-1 shadow-2xl"
      >
        {desktopItems.map(({ key, path, labelKey, icon: Icon }) => {
          const active = pathname === path;
          return (
            <Link
              key={key}
              to={path}
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                active ? "bg-white/15 text-white" : "text-white/75 hover:text-white"
              }`}
            >
              <Icon size={16} />
              <span className="text-xs font-semibold">{t(labelKey)}</span>
            </Link>
          );
        })}
      </nav>

      {/* Floating Map FAB — hidden on /map itself and on pages that render their own map FAB */}
      {!pathname.startsWith("/map") && !hideGlobalMapFab && (
        <Link
          to="/map"
          aria-label="Open map view"
          className="group fixed z-[95] right-3.5 md:right-8 flex flex-col items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-navy via-royal to-navy-deep text-primary-foreground animate-wave-sweep shadow-[0_8px_20px_-5px_rgba(15,23,42,0.4),0_6px_8px_-4px_rgba(15,23,42,0.4)] hover:scale-105 active:scale-95 transition-transform duration-300"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)" }}
        >
          <span className="absolute inset-0 rounded-full bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />
          <MapIcon className="relative z-10 size-5 md:size-6 mb-0.5 drop-shadow-sm transition-transform duration-300 group-hover:-translate-y-0.5" />
          <span className="relative z-10 text-[9px] md:text-[10px] font-bold tracking-wider uppercase drop-shadow-sm">
            <AutoT text="Map" />
          </span>
        </Link>
      )}
    </>
  );
}
